import { Fragment, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Search, RefreshCw, Play, Pause, ChevronRight, ChevronDown, Download, Trophy, Users, Zap, Sparkles, Columns, X as XIcon, DollarSign } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import HelpBubble from "../components/HelpBubble";
import LaunchCampaignModal from "../components/LaunchCampaignModal";
import S1HealthPanel from "../components/S1HealthPanel";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";

const fmt = new Intl.NumberFormat("en-US");
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

const DATE_PRESETS = [
  { label: "Today", days: 0 },
  { label: "Yesterday", days: 1, offset: 1 },
  { label: "7d", days: 7 },
  { label: "14d", days: 14 },
  { label: "30d", days: 30 },
];

function isoOffset(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function KpiCard({ label, value, sub, positive, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">{label}</div>
      <div className="mt-1 text-2xl font-mono font-semibold tracking-tight" style={{ fontFamily: "JetBrains Mono, monospace" }}>
        {value}
      </div>
      {sub !== undefined && (
        <div className={`text-xs mt-1 font-mono ${positive ? "text-emerald-600" : "text-slate-500"}`}>{sub}</div>
      )}
    </div>
  );
}

function AutomationSavingsCard() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const [window, setWindow] = useState("24h");

  useEffect(() => {
    api.get(`/optimize/automation-savings?window=${window}`)
      .then(({ data }) => setData(data))
      .catch((e) => console.error(e));
  }, [window]);

  const total = data?.total_saved_usd || 0;
  const hasActivity = total > 0;

  const WINDOWS = [
    { key: "24h", label: "24h" },
    { key: "7d", label: "7d" },
    { key: "30d", label: "30d" },
    { key: "mtd", label: "MTD" },
  ];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="kpi-automation-savings"
        className={`text-left rounded-md p-4 border transition-all ${
          hasActivity
            ? "bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 hover:border-blue-400 hover:shadow-sm"
            : "bg-white border-slate-200 hover:border-slate-300"
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-blue-700 font-mono">Automation saved · {window}</div>
          <Zap className={`w-3.5 h-3.5 ${hasActivity ? "text-blue-600" : "text-slate-400"}`} />
        </div>
        <div className="mt-1 text-2xl font-mono font-semibold tracking-tight text-blue-700" style={{ fontFamily: "JetBrains Mono, monospace" }}>
          {data ? fmtMoney(total) : "—"}
        </div>
        <div className="text-xs mt-1 font-mono text-slate-500">
          {hasActivity ? "click for breakdown →" : "arm Auto-Operator to start saving"}
        </div>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[440px] sm:max-w-[440px] bg-white overflow-y-auto" data-testid="automation-savings-sheet">
          <SheetHeader className="pb-3 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-blue-600 font-mono">{data?.window_label || "Loading…"}</div>
            <SheetTitle className="text-2xl text-slate-900" data-testid="savings-total">Automation saved {fmtMoney(total)}</SheetTitle>
          </SheetHeader>

          <div className="flex items-center gap-1 bg-slate-50 border border-slate-200 rounded-md p-0.5 mt-4 w-fit" data-testid="savings-window-tabs">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setWindow(w.key)}
                data-testid={`savings-win-${w.key}`}
                className={`px-3 py-1 text-xs rounded font-mono transition-colors ${
                  window === w.key ? "bg-blue-600 text-white" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>

          {data && (
            <div className="mt-4 space-y-3">
              {[
                { key: "exclusions", label: "Exclusion audiences", color: "text-amber-600", bar: "bg-amber-500", icon: "👁" },
                { key: "winner_scales", label: "Winner scales", color: "text-emerald-600", bar: "bg-emerald-500", icon: "🚀" },
                { key: "circuit_breaker", label: "Circuit breaker saves", color: "text-red-600", bar: "bg-red-500", icon: "🛡" },
              ].map(({ key, label, color, bar, icon }) => {
                const b = data.breakdown?.[key] || {};
                const count = b.adsets_protected ?? b.events ?? 0;
                const share = total > 0 ? Math.round(((b.value_usd || 0) / total) * 100) : 0;
                return (
                  <div key={key} className="bg-slate-50 border border-slate-200 rounded-md p-3" data-testid={`savings-${key}`}>
                    <div className="flex items-center justify-between">
                      <div className="text-sm text-slate-900 font-medium"><span className="mr-1">{icon}</span> {label}</div>
                      <div className={`text-sm font-mono font-semibold ${color}`}>{fmtMoney(b.value_usd || 0)}</div>
                    </div>
                    <div className="mt-1.5 h-1.5 rounded-full bg-slate-200 overflow-hidden">
                      <div className={`h-full ${bar}`} style={{ width: `${share}%` }} />
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono mt-1.5">
                      {share}% · {count} event{count === 1 ? "" : "s"} · {b.assumption}
                    </div>
                  </div>
                );
              })}
              <div className="text-[10px] text-slate-500 font-mono pt-3 border-t border-slate-200 leading-relaxed">
                Figures are conservative projections based on industry RSOC norms. They represent spend <i>not wasted</i> + profit <i>projected</i> — not cash delivered. Use as a directional ROI signal for the automation stack.
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </>
  );
}

function CostSummaryCard() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const load = () =>
      api.get("/costs/today").then(({ data }) => setD(data)).catch(() => {});
    load();
    const t = setInterval(load, 60_000); // refresh every minute
    return () => clearInterval(t);
  }, []);

  const ai = d?.ai_spend_usd ?? 0;
  const fb = d?.fb_spend_usd ?? 0;
  const total = (ai + fb).toFixed(2);
  const profit = d?.net_profit_usd ?? 0;
  const profitPositive = profit >= 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="kpi-costs-today"
        className="text-left bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-md p-4 hover:border-slate-400 hover:shadow-sm transition-all"
        title="AI cost (briefs + images + videos) + FB ad spend today"
      >
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Today · all-in</div>
          <DollarSign className="w-3.5 h-3.5 text-slate-400" />
        </div>
        <div
          className="mt-1 text-2xl font-mono font-semibold tracking-tight text-slate-900"
          style={{ fontFamily: "JetBrains Mono, monospace" }}
          data-testid="cost-today-total"
        >
          ${total}
        </div>
        <div className="text-xs mt-1 font-mono text-slate-500">
          AI <span className="text-slate-900" data-testid="cost-today-ai">${ai.toFixed(2)}</span>
          {" · "}
          FB <span className="text-slate-900" data-testid="cost-today-fb">${fb.toFixed(2)}</span>
        </div>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[440px] sm:max-w-[440px] bg-white overflow-y-auto" data-testid="cost-summary-sheet">
          <SheetHeader className="pb-3 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono">Today · {d?.day || "—"}</div>
            <SheetTitle className="text-2xl text-slate-900">Spend breakdown</SheetTitle>
          </SheetHeader>

          <div className="mt-5 space-y-4">
            {/* Profit badge */}
            <div
              className={`rounded-md p-4 border ${profitPositive ? "bg-emerald-50 border-emerald-200" : "bg-red-50 border-red-200"}`}
              data-testid="cost-net-profit"
            >
              <div className={`text-[10px] uppercase tracking-wider font-mono ${profitPositive ? "text-emerald-700" : "text-red-700"}`}>
                Net profit today
              </div>
              <div className={`text-3xl font-mono font-semibold mt-1 ${profitPositive ? "text-emerald-700" : "text-red-700"}`}>
                {profitPositive ? "+" : ""}${profit.toFixed(2)}
              </div>
              <div className="text-[11px] font-mono text-slate-600 mt-1">
                Revenue ${d?.fb_revenue_usd?.toFixed(2) || "0.00"} − FB ${fb.toFixed(2)} − AI ${ai.toFixed(2)}
              </div>
            </div>

            {/* AI breakdown */}
            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">AI ($${ai.toFixed(2)}) · manual usage only</div>
              {[
                { label: "Text (briefs · keywords)", usd: d?.ai_breakdown?.text_usd, calls: d?.ai_calls?.text, key: "text" },
                { label: "Images (GPT Image 1)", usd: d?.ai_breakdown?.image_usd, calls: d?.ai_calls?.image, key: "image" },
                { label: "Video (Sora · fal.ai)", usd: d?.ai_breakdown?.video_usd, calls: d?.ai_calls?.video, key: "video" },
              ].map((r) => (
                <div key={r.key} className="flex items-center justify-between py-1 text-xs" data-testid={`cost-ai-${r.key}`}>
                  <span className="text-slate-600">{r.label}</span>
                  <span className="font-mono text-slate-900">
                    ${(r.usd ?? 0).toFixed(2)}{" "}
                    <span className="text-slate-400">· {r.calls ?? 0} call{(r.calls ?? 0) === 1 ? "" : "s"}</span>
                  </span>
                </div>
              ))}
            </div>

            {/* FB breakdown */}
            <div className="bg-white border border-slate-200 rounded-md p-4">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2">Facebook Ads</div>
              <div className="flex items-center justify-between py-1 text-xs">
                <span className="text-slate-600">Ad spend</span>
                <span className="font-mono text-slate-900">${fb.toFixed(2)}</span>
              </div>
              <div className="flex items-center justify-between py-1 text-xs">
                <span className="text-slate-600">Active campaigns</span>
                <span className="font-mono text-slate-900">{d?.fb_active_campaigns ?? 0}</span>
              </div>
              <div className="flex items-center justify-between py-1 text-xs">
                <span className="text-slate-600">S1 revenue</span>
                <span className="font-mono text-slate-900">${d?.fb_revenue_usd?.toFixed(2) || "0.00"}</span>
              </div>
            </div>

            <p className="text-[11px] text-slate-500 font-mono leading-relaxed">
              AI cost auto-refreshes every minute. FB spend comes from your real ad account
              (no mocks). AI is manual-click only — no background jobs burn credits.
            </p>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}



function LLMHealthBadge() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = () => {
    api.get("/llm/health").then(({ data }) => setData(data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;
  const provider = data.provider;
  const isEmergent = provider === "emergent";
  const today = data.today || {};
  const month = data.month_30d || {};
  const todayCost = Number(today.cost_usd || 0);
  const monthCost = Number(month.cost_usd || 0);
  const dotColor = isEmergent ? "bg-indigo-500" : "bg-emerald-500";
  const providerLabel = isEmergent ? "Universal Key" : "Direct";

  return (
    <>
      <button
        type="button"
        onClick={() => { setOpen(true); load(); }}
        data-testid="llm-health-badge"
        title={`LLM provider: ${providerLabel} · click for today's spend`}
        className="flex items-center gap-2 bg-white border border-slate-200 hover:border-indigo-400 hover:bg-indigo-50 text-slate-700 rounded-md px-2.5 py-1.5 text-xs transition-colors font-mono"
      >
        <Sparkles className="w-3.5 h-3.5 text-indigo-500" />
        <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`}></span>
        <span className="text-slate-900 font-medium">{providerLabel}</span>
        <span className="text-slate-400">·</span>
        <span className="text-emerald-600">${todayCost.toFixed(2)}</span>
        <span className="text-[10px] text-slate-400">today</span>
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="w-[420px] sm:max-w-[440px] bg-white overflow-y-auto" data-testid="llm-health-sheet">
          <SheetHeader>
            <SheetTitle className="text-slate-900 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-indigo-500" /> LLM provider health
            </SheetTitle>
          </SheetHeader>

          <div className="mt-4 space-y-4">
            <div className={`rounded-md p-3 border ${isEmergent ? "bg-indigo-50 border-indigo-200" : "bg-emerald-50 border-emerald-200"}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500">Current mode</div>
                  <div className="text-lg font-semibold text-slate-900">{providerLabel}</div>
                </div>
                <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${isEmergent ? "bg-indigo-500 text-white" : "bg-emerald-500 text-white"}`}>
                  {provider.toUpperCase()}
                </span>
              </div>
              <div className="mt-2 text-[11px] font-mono text-slate-600 space-y-0.5">
                <div>claude · <span className="text-slate-900">{data.claude_model}</span></div>
                <div>image · <span className="text-slate-900">{data.image_model}</span></div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-2">Today</div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] text-slate-500 font-mono">spend</div>
                  <div className="text-lg font-semibold text-emerald-600" data-testid="llm-today-cost">${todayCost.toFixed(4)}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] text-slate-500 font-mono">text calls</div>
                  <div className="text-lg font-semibold text-slate-900">{today.text_calls || 0}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] text-slate-500 font-mono">images</div>
                  <div className="text-lg font-semibold text-slate-900">{today.images_generated || 0}</div>
                </div>
                <div className="bg-white border border-slate-200 rounded p-2.5">
                  <div className="text-[10px] text-slate-500 font-mono">tokens in/out</div>
                  <div className="text-xs font-mono text-slate-900">
                    {(today.text_input_tokens || 0).toLocaleString()} / {(today.text_output_tokens || 0).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-wider font-mono text-slate-500 mb-2">Last 30 days</div>
              <div className="bg-white border border-slate-200 rounded p-3">
                <div className="flex items-baseline justify-between">
                  <div className="text-2xl font-semibold text-slate-900" data-testid="llm-month-cost">${monthCost.toFixed(2)}</div>
                  <div className="text-[10px] text-slate-500 font-mono">{month.days_with_usage || 0}d active</div>
                </div>
                <div className="mt-1 flex gap-3 text-[11px] font-mono text-slate-500">
                  <span>{(month.text_calls || 0).toLocaleString()} text calls</span>
                  <span>·</span>
                  <span>{(month.images_generated || 0).toLocaleString()} images</span>
                </div>
              </div>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded p-2.5 text-[11px] font-mono text-amber-800">
              {data.pricing_note}
            </div>

            {isEmergent && (
              <div className="text-[11px] text-slate-600 leading-relaxed">
                <div className="font-semibold text-slate-900 mb-1">Ready to switch to direct provider keys?</div>
                Get an Anthropic key at <span className="text-blue-600">console.anthropic.com</span> and a Gemini key at <span className="text-blue-600">aistudio.google.com/apikey</span>, then set <code className="bg-slate-100 px-1 rounded">LLM_PROVIDER=direct</code> and restart the backend. ~20% cost savings at volume.
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}

function ConnectionHealthChip({ status }) {
  // status: { facebook: bool|null, system1: bool|null }
  const checking = status.facebook === null || status.system1 === null;
  const allGood = status.facebook && status.system1;
  const anyMissing = !checking && (!status.facebook || !status.system1);

  const dotClass = (v) =>
    v === null ? "bg-slate-300" : v ? "bg-emerald-500" : "bg-red-500";

  const pillClass = checking
    ? "border-slate-200 text-slate-500 bg-white"
    : allGood
      ? "border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
      : "border-red-200 text-red-700 bg-red-50 hover:bg-red-100";

  const title = checking
    ? "Checking connections…"
    : allGood
      ? "Facebook + System1 connected"
      : !status.facebook && !status.system1
        ? "Facebook and System1 not connected · click to fix"
        : !status.facebook
          ? "Facebook not connected · click to fix"
          : "System1 not connected · click to fix";

  return (
    <a
      href="/connections"
      title={title}
      data-testid="conn-health-chip"
      className={`inline-flex items-center gap-2 rounded-full border px-2.5 py-1 text-[11px] font-mono transition-colors ${pillClass}`}
    >
      <span className="inline-flex items-center gap-1" data-testid="conn-health-fb">
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass(status.facebook)}`} />
        FB
      </span>
      <span className="text-slate-300">·</span>
      <span className="inline-flex items-center gap-1" data-testid="conn-health-s1">
        <span className={`w-1.5 h-1.5 rounded-full ${dotClass(status.system1)}`} />
        S1
      </span>
      {anyMissing && <span className="text-[10px] font-semibold ml-0.5">Fix →</span>}
    </a>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState({ rows: [], totals: {}, using_mock: false });
  const [leaderboard, setLeaderboard] = useState([]);
  const [allBuyers, setAllBuyers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [connStatus, setConnStatus] = useState({ facebook: null, system1: null });
  const [lastSynced, setLastSynced] = useState(null);
  const [range, setRange] = useState({ start: isoOffset(7), end: isoOffset(0), label: "7d" });
  const [vertical, setVertical] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [adAccounts, setAdAccounts] = useState([]);
  const [adAccountId, setAdAccountId] = useState(() => localStorage.getItem("dash_ad_account") || "all");
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("dash_view_mode") || "simple");
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [selected, setSelected] = useState({}); // {campaignId: true}
  const [bulkBudget, setBulkBudget] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  const [circuitState, setCircuitState] = useState({ open_count: 0, accounts: [] });
  const [showCircuitDetails, setShowCircuitDetails] = useState(false);

  // All 17 data columns (name + status are always-on; these are the toggleable ones)
  const ALL_COLUMNS = useMemo(() => [
    { key: "vertical", label: "Vertical" },
    { key: "daily_budget", label: "Budget" },
    { key: "spend", label: "Spent" },
    { key: "impressions", label: "Impressions" },
    { key: "cpm", label: "CPM" },
    { key: "clicks", label: "Clicks" },
    { key: "cpc", label: "CPC" },
    { key: "ctr", label: "CTR" },
    // Meta-attribution block (what Meta Ads Manager owns)
    { key: "ad_conversions", label: "Ad Conv" },
    { key: "cpaa", label: "CPAA" },
    { key: "ad_rpc", label: "Ad RPC" },
    { key: "ad_conv_rate", label: "Ad CONV%" },
    // System1-attribution block (postback truth)
    { key: "conversions", label: "Conv (S1)" },
    { key: "cost_per_conv", label: "CPA" },
    { key: "rpc", label: "RPC" },
    { key: "s1_conv_rate", label: "CONV%" },
    { key: "revenue", label: "Revenue" },
    { key: "s1_clicks", label: "S1 Clicks" },
    { key: "rpm", label: "RPM" },
    { key: "rpwidget", label: "Widget Srch" },
    // Business outcome
    { key: "net_profit", label: "Profit" },
    { key: "roi", label: "ROI" },
    { key: "roas", label: "ROAS" },
    { key: "rpac", label: "RPAC" },
  ], []);

  // Default = simple picks ~10 most-used; detailed = all. User override via localStorage.
  const DEFAULT_SIMPLE_KEYS = ["vertical", "daily_budget", "spend", "cpc", "conversions", "revenue", "rpc", "net_profit", "roi"];
  const DEFAULT_DETAILED_KEYS = ALL_COLUMNS.map((c) => c.key);

  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const stored = localStorage.getItem("dash_visible_cols");
      if (stored) return new Set(JSON.parse(stored));
    } catch (e) { /* fall through */ }
    return new Set(DEFAULT_SIMPLE_KEYS);
  });
  const toggleCol = (key) => {
    const next = new Set(visibleCols);
    if (next.has(key)) next.delete(key); else next.add(key);
    setVisibleCols(next);
    try { localStorage.setItem("dash_visible_cols", JSON.stringify([...next])); } catch (e) { /* ignore */ }
  };
  const isVisible = (key) => visibleCols.has(key);

  // When user flips Simple/Detailed toggle, reset columns to that preset
  const applyViewMode = (mode) => {
    setViewMode(mode);
    const keys = mode === "detailed" ? DEFAULT_DETAILED_KEYS : DEFAULT_SIMPLE_KEYS;
    setVisibleCols(new Set(keys));
    try { localStorage.setItem("dash_visible_cols", JSON.stringify(keys)); } catch (e) { /* ignore */ }
  };

  // Remember selections across reloads
  useEffect(() => { localStorage.setItem("dash_ad_account", adAccountId); }, [adAccountId]);
  useEffect(() => { localStorage.setItem("dash_view_mode", viewMode); }, [viewMode]);

  // Fetch connection status fast & independently of /campaigns so the
  // banner never lies to non-admins while FB is rate-limited.
  const loadConnStatus = async () => {
    try {
      const { data: conn } = await api.get("/connections/status");
      setConnStatus({
        facebook: !!conn?.facebook,
        system1: !!conn?.system1,
      });
      if (conn?.fb_ad_accounts) setAdAccounts(conn.fb_ad_accounts);
    } catch (e) {
      console.error(e);
      // Leave as null so we don't wrongly claim "not connected"
      setConnStatus((s) => ({ facebook: s.facebook, system1: s.system1 }));
    }
  };

  const load = async () => {
    setLoading(true);
    // Watchdog: never leave the spinner on forever (FB can stall up to 30s).
    const watchdog = setTimeout(() => setLoading(false), 35000);
    try {
      const { data } = await api.get("/campaigns", {
        params: {
          start_date: range.start, end_date: range.end,
          vertical, search, level: "campaign",
          ad_account_id: adAccountId !== "all" ? adAccountId : undefined,
          all_buyers: isAdmin && allBuyers ? true : undefined,
        },
      });
      setData(data);
      try {
        const { data: s } = await api.get("/sync/status");
        setLastSynced(s.last_synced_at);
      } catch (e) { console.error(e); }
      if (isAdmin) {
        try {
          const { data: lb } = await api.get("/team/leaderboard", {
            params: { start_date: range.start, end_date: range.end },
          });
          setLeaderboard(lb.leaderboard || []);
        } catch (e) { console.error(e); }
      }
    } catch (e) {
      toast.error("Failed to load campaigns");
    } finally {
      clearTimeout(watchdog);
      setLoading(false);
    }
  };

  const syncNow = async () => {
    // Keep the /sync/trigger spinner separate from /campaigns so the Sync
    // button resolves in <1s even when FB is slow.
    setSyncing(true);
    try {
      const { data: s } = await api.post("/sync/trigger");
      setLastSynced(s.last_synced_at);
      toast.success("Synced");
    } catch {
      toast.error("Sync failed");
    } finally {
      setSyncing(false);
    }
    // Refresh data separately — driven by `loading`, not `syncing`.
    load();
  };

  const exportCsv = async () => {
    try {
      const resp = await api.get("/campaigns/export", {
        params: { start_date: range.start, end_date: range.end, vertical, level: "campaign" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `rsoc_${range.start}_${range.end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [range, vertical, allBuyers, adAccountId]);
  useEffect(() => { loadConnStatus(); /* eslint-disable-next-line */ }, []);

  // Poll circuit state right after each load + every 45s so the "skipped"
  // pill stays current without flooding the API. The endpoint returns only
  // ad accounts on this user's connection so it's cheap.
  const loadCircuitState = async () => {
    try {
      const { data: cs } = await api.get("/fb-circuit-state");
      setCircuitState(cs || { open_count: 0, accounts: [] });
    } catch (e) {
      // Non-fatal: keep last known state so UI doesn't flicker.
      console.debug("circuit-state poll failed", e);
    }
  };
  useEffect(() => {
    loadCircuitState();
    const t = setInterval(loadCircuitState, 45000);
    return () => clearInterval(t);
  }, [loading]);

  const verticals = useMemo(() => {
    const s = new Set(data.rows.map((r) => r.vertical).filter(Boolean));
    return ["all", ...Array.from(s)];
  }, [data.rows]);

  const filtered = useMemo(() => {
    if (!search) return data.rows;
    const q = search.toLowerCase();
    return data.rows.filter((r) => r.name.toLowerCase().includes(q));
  }, [data.rows, search]);

  const toggleExpand = async (row) => {
    const id = row.id;
    if (expanded[id]) {
      setExpanded((x) => ({ ...x, [id]: null }));
      return;
    }
    const { data: ad } = await api.get("/campaigns", {
      params: { start_date: range.start, end_date: range.end, level: "adset" },
    });
    const children = ad.rows.filter((r) => r.parent_id === id);
    setExpanded((x) => ({ ...x, [id]: children }));
  };

  const saveBudget = async (row) => {
    try {
      await api.post(`/campaigns/${row.id}/budget`, { daily_budget: parseFloat(editValue) });
      toast.success(`Budget → ${fmtMoney(editValue)}`);
      setEditing(null);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  const toggleStatus = async (row) => {
    const next = row.status === "ACTIVE" ? "PAUSED" : "ACTIVE";
    try {
      await api.post(`/campaigns/${row.id}/status`, { status: next });
      toast.success(`${row.name} → ${next}`);
      load();
    } catch {
      toast.error("Status update failed");
    }
  };

  return (
    <div className="p-6 max-w-[1600px]" data-testid="dashboard-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" style={{ fontFamily: "Manrope" }}>Performance <HelpBubble feature="kpis" /></h1>
          <p className="text-xs text-slate-500 mt-1 font-mono">
            {data.using_mock
              ? "demo data · connect Facebook to see live metrics"
              : (data.rows?.length
                ? "live · synced from Facebook + System1"
                : connStatus.facebook === null
                  ? "checking connections…"
                  : connStatus.facebook && connStatus.system1
                    ? "connected · no campaign data in this range yet"
                    : !connStatus.facebook && !connStatus.system1
                      ? "connect Facebook + System1 to begin"
                      : !connStatus.facebook
                        ? "Facebook not connected"
                        : "System1 not connected")}
            {lastSynced && <span className="ml-2 text-slate-400">· last synced {new Date(lastSynced).toLocaleTimeString()}</span>}
          </p>
          {circuitState.open_count > 0 && (
            <div className="mt-2 flex items-start gap-2" data-testid="circuit-pill">
              <button
                type="button"
                onClick={() => setShowCircuitDetails((v) => !v)}
                className="inline-flex items-center gap-1.5 text-[11px] font-mono bg-amber-50 border border-amber-200 text-amber-800 hover:bg-amber-100 rounded-full px-2.5 py-0.5 transition-colors"
                data-testid="circuit-pill-btn"
                title="One or more ad accounts temporarily skipped after repeated failures"
              >
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse" />
                {circuitState.open_count} ad account{circuitState.open_count === 1 ? "" : "s"} temporarily skipped
                <span className="text-amber-600">· {showCircuitDetails ? "hide" : "details"}</span>
              </button>
            </div>
          )}
          {showCircuitDetails && circuitState.open_count > 0 && (
            <div
              className="mt-2 rounded-md border border-amber-200 bg-amber-50/60 p-3 text-xs max-w-xl"
              data-testid="circuit-pill-details"
            >
              <div className="text-slate-700 mb-2 leading-relaxed">
                These ad accounts returned repeated errors from Facebook, so we're skipping them for
                a short cool-down to keep the dashboard fast. They'll retry automatically.
              </div>
              <ul className="space-y-1.5">
                {circuitState.accounts.filter((a) => a.is_open).map((a) => {
                  const mins = Math.floor((a.remaining_s || 0) / 60);
                  const secs = (a.remaining_s || 0) % 60;
                  return (
                    <li key={a.ad_account_id} className="flex items-center justify-between gap-2">
                      <span className="font-mono text-slate-800 truncate">{a.name || a.ad_account_id}</span>
                      <span className="font-mono text-amber-700 shrink-0">
                        retry in {mins > 0 ? `${mins}m ${secs}s` : `${secs}s`}
                      </span>
                    </li>
                  );
                })}
              </ul>
              {isAdmin && (
                <div className="mt-2 flex items-center gap-3 text-[11px] font-mono">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await api.post("/admin/fb-circuit-state/reset", {});
                        await loadCircuitState();
                        await load();
                        toast.success("Circuit reset · retrying all ad accounts");
                      } catch {
                        toast.error("Reset failed");
                      }
                    }}
                    className="text-blue-600 hover:text-blue-700 underline"
                    data-testid="circuit-reset-btn"
                  >
                    Reset now → retry all
                  </button>
                  <a
                    href="/admin/logs"
                    className="text-slate-500 hover:text-slate-700 underline"
                    data-testid="circuit-debug-link"
                  >
                    Debug on /admin/logs →
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="flex gap-2 items-center">
        <ConnectionHealthChip status={connStatus} />
        {isAdmin && <LLMHealthBadge />}
        <button
          onClick={load}
          data-testid="refresh-btn"
          disabled={loading}
          className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-60 disabled:cursor-not-allowed text-slate-900 rounded-md px-3 py-1.5 text-sm transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
        <button
          onClick={syncNow}
          data-testid="sync-now-btn"
          disabled={syncing || loading}
          className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-60 disabled:cursor-not-allowed text-slate-900 rounded-md px-3 py-1.5 text-sm transition-colors"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${syncing || loading ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync now"}
        </button>
        <button
          onClick={exportCsv}
          data-testid="export-csv-btn"
          className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-100 text-slate-900 rounded-md px-3 py-1.5 text-sm transition-colors"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      </div>

      {/* System1 Revenue Health — live diagnostic */}
      <S1HealthPanel />

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2 mb-4 sticky top-0 z-10 py-2 bg-white">
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-0.5">
          {DATE_PRESETS.map((p) => (
            <button
              key={p.label}
              onClick={() => {
                const start = p.offset ? isoOffset(p.offset) : isoOffset(p.days || 0);
                const end = p.offset ? isoOffset(p.offset) : isoOffset(0);
                setRange({ start, end, label: p.label });
              }}
              data-testid={`date-${p.label}`}
              className={`px-2.5 py-1 text-xs rounded font-mono transition-colors ${
                range.label === p.label ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        <select
          value={vertical}
          onChange={(e) => setVertical(e.target.value)}
          data-testid="vertical-filter"
          className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500/40"
        >
          {verticals.map((v) => (
            <option key={v} value={v}>{v === "all" ? "All verticals" : v}</option>
          ))}
        </select>

        {adAccounts.length > 0 && (
          <select
            value={adAccountId}
            onChange={(e) => setAdAccountId(e.target.value)}
            data-testid="ad-account-filter"
            className="bg-white border border-slate-200 rounded-md px-3 py-1.5 text-sm text-slate-900 focus:outline-none focus:ring-1 focus:ring-blue-500/40 max-w-[220px]"
            title="Filter by Facebook ad account"
          >
            <option value="all">All ad accounts ({adAccounts.length})</option>
            {adAccounts.map((a) => (
              <option key={a.id} value={a.id}>{a.name || a.id}</option>
            ))}
          </select>
        )}

        <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex bg-slate-100 border border-slate-200 rounded-md p-0.5" data-testid="view-mode-toggle">
            {["simple", "detailed"].map((m) => (
              <button
                key={m}
                onClick={() => applyViewMode(m)}
                data-testid={`view-mode-${m}`}
                className={`px-3 py-1 text-xs font-medium rounded capitalize transition-colors ${
                  viewMode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-900"
                }`}
              >
                {m}
              </button>
            ))}
          </div>

          {/* Column picker */}
          <div className="relative">
            <button
              onClick={() => setShowColumnPicker((v) => !v)}
              data-testid="column-picker-btn"
              className="flex items-center gap-1.5 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-md px-3 py-1.5 text-xs font-medium transition-colors"
              title="Toggle visible columns (saved per buyer)"
            >
              <Columns className="w-3 h-3" /> Columns ({visibleCols.size})
            </button>
            {showColumnPicker && (
              <>
                <div className="fixed inset-0 z-30" onClick={() => setShowColumnPicker(false)} />
                <div
                  data-testid="column-picker-popover"
                  className="absolute right-0 mt-1 z-40 w-64 bg-white border border-slate-200 rounded-md shadow-lg p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-[11px] font-semibold text-slate-900 uppercase tracking-wider">Columns</div>
                    <button
                      onClick={() => setShowColumnPicker(false)}
                      className="text-slate-400 hover:text-slate-900"
                      data-testid="close-column-picker"
                    >
                      <XIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
                    {ALL_COLUMNS.map((c) => (
                      <label
                        key={c.key}
                        data-testid={`col-toggle-${c.key}`}
                        className="flex items-center gap-2 text-xs text-slate-700 hover:bg-slate-50 rounded px-2 py-1 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={isVisible(c.key)}
                          onChange={() => toggleCol(c.key)}
                          className="accent-blue-600"
                        />
                        <span>{c.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="flex gap-1.5 mt-2 pt-2 border-t border-slate-100">
                    <button
                      onClick={() => applyViewMode("simple")}
                      data-testid="preset-simple-btn"
                      className="flex-1 text-[10px] font-mono px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded"
                    >
                      Reset · Simple
                    </button>
                    <button
                      onClick={() => applyViewMode("detailed")}
                      data-testid="preset-detailed-btn"
                      className="flex-1 text-[10px] font-mono px-2 py-1 bg-slate-100 hover:bg-slate-200 rounded"
                    >
                      All 17
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>

          {isAdmin && (
            <button
              onClick={() => setShowLaunchModal(true)}
              data-testid="launch-campaign-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md px-3 py-1.5 flex items-center gap-1.5 transition-colors"
              title="Launch a new campaign manually (paused)"
            >
              <Zap className="w-3 h-3" /> New campaign
            </button>
          )}
        </div>

        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search campaigns…"
            data-testid="search-input"
            className="w-full bg-white border border-slate-200 rounded-md pl-9 pr-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
        </div>
      </div>

      {/* FB API slow banner — when backend wait_for timed out on the thread. */}
      {data.timed_out && (
        <div
          data-testid="fb-slow-banner"
          className="mb-4 border border-amber-200 bg-amber-50 rounded-md p-4 flex items-start gap-3"
        >
          <div className="bg-amber-500 text-white rounded-full p-1.5 shrink-0">
            <RefreshCw className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900">Facebook API is slow right now</div>
            <p className="text-xs text-slate-600 mt-0.5">
              We timed out waiting for campaign data (15s). Your data is safe — click
              <b> Refresh</b> in a moment to retry. If this keeps happening on a 30d range,
              try a shorter range like <b>7d</b> or filter to a single ad account.
            </p>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="fb-slow-retry"
            className="bg-amber-600 hover:bg-amber-700 disabled:opacity-60 text-white text-xs font-medium rounded-md px-3 py-1.5 shrink-0 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Empty-state CTA banner — only when something is actually NOT connected */}
      {!loading && !data.using_mock && !data.timed_out && (!data.rows || data.rows.length === 0)
        && connStatus.facebook !== null
        && (!connStatus.facebook || !connStatus.system1) && (
        <div
          data-testid="connect-fb-banner"
          className="mb-4 border border-blue-200 bg-blue-50 rounded-md p-4 flex items-start gap-3"
        >
          <div className="bg-blue-600 text-white rounded-full p-1.5 shrink-0">
            <Zap className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-slate-900">
              {!connStatus.facebook && !connStatus.system1
                ? "Connect Facebook + System1 to start pulling live data"
                : !connStatus.facebook
                  ? "Facebook not connected"
                  : "System1 not connected"}
            </div>
            <p className="text-xs text-slate-600 mt-0.5">
              {!connStatus.facebook && !connStatus.system1
                ? <>Add your Facebook Ad account and System1 keys on the <a href="/connections" className="text-blue-700 underline font-medium">Connections</a> page to start pulling live spend, revenue and ROI.</>
                : !connStatus.facebook
                  ? <>Connect your Facebook Ad account on the <a href="/connections" className="text-blue-700 underline font-medium">Connections</a> page to pull live spend & metrics.</>
                  : <>Add the workspace System1 key on the <a href="/connections" className="text-blue-700 underline font-medium">Connections</a> page so revenue can post back to this dashboard.</>}
            </p>
          </div>
          <a
            href="/connections"
            data-testid="connect-fb-cta"
            className="bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-md px-3 py-1.5 shrink-0 transition-colors"
          >
            Connect now
          </a>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
        <KpiCard label="FB Spend" value={fmtMoney(data.totals.spend)} sub={`${fmt.format(data.totals.impressions || 0)} impr.`} testid="kpi-spend" />
        <KpiCard label="S1 Revenue" value={fmtMoney(data.totals.revenue)} sub={`${fmt.format(data.totals.conversions || 0)} conv.`} testid="kpi-revenue" />
        <KpiCard label="Net Profit" value={fmtMoney(data.totals.net_profit)} sub={`CPC ${fmtMoney(data.totals.cpc)} · RPC ${fmtMoney(data.totals.rpc)}`} positive={Number(data.totals.net_profit) > 0} testid="kpi-profit" />
        <KpiCard label="ROI" value={fmtPct(data.totals.roi)} sub={data.totals.roi > 0 ? "profitable" : "unprofitable"} positive={Number(data.totals.roi) > 0} testid="kpi-roi" />
        <AutomationSavingsCard />
        <CostSummaryCard />
      </div>

      {/* Admin: Buyer toggle + Leaderboard */}
      {isAdmin && (
        <>
          <div className="flex items-center gap-2 mb-3" data-testid="admin-controls">
            <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-md p-0.5">
              <button
                onClick={() => setAllBuyers(false)}
                data-testid="scope-mine"
                className={`px-3 py-1 text-xs rounded font-mono transition-colors ${!allBuyers ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"}`}
              >My campaigns</button>
              <button
                onClick={() => setAllBuyers(true)}
                data-testid="scope-team"
                className={`flex items-center gap-1 px-3 py-1 text-xs rounded font-mono transition-colors ${allBuyers ? "bg-blue-600 text-white" : "text-slate-500 hover:text-slate-900"}`}
              ><Users className="w-3 h-3" /> All buyers</button>
            </div>
            {allBuyers && <span className="text-[11px] text-slate-500 font-mono">showing aggregated workspace performance · buyer column added</span>}
          </div>

          {leaderboard.length > 1 && (
            <div className="bg-white border border-slate-200 rounded-md p-4 mb-4" data-testid="leaderboard">
              <div className="flex items-center gap-2 mb-3 text-[11px] uppercase tracking-wider text-amber-600 font-mono">
                <Trophy className="w-3.5 h-3.5" /> Buyer Leaderboard · {range.label}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {leaderboard.map((b) => (
                  <div key={b.user_id} data-testid={`lb-${b.user_id}`} className="bg-white border border-slate-200 rounded p-3 flex items-center gap-3">
                    <div className={`text-lg font-mono font-bold w-6 text-center ${b.rank === 1 ? "text-amber-600" : b.rank === 2 ? "text-slate-600" : b.rank === 3 ? "text-amber-700" : "text-slate-500"}`}>
                      #{b.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-slate-900 truncate">{b.name}</div>
                      <div className="text-[10px] text-slate-500 font-mono">{b.campaigns} camp · W{b.winners}/L{b.losers}</div>
                    </div>
                    <div className="text-right">
                      <div className={`text-sm font-mono font-medium ${b.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(b.net_profit)}</div>
                      <div className={`text-[10px] font-mono ${b.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(b.roi)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Bulk-edit budget bar (admin only, appears when ≥1 campaign selected) */}
      {isAdmin && Object.values(selected).filter(Boolean).length > 0 && (
        <div
          data-testid="bulk-edit-bar"
          className="sticky top-12 z-20 mb-3 bg-blue-50 border border-blue-300 rounded-md px-4 py-2.5 flex items-center gap-3 shadow-sm"
        >
          <div className="text-xs text-slate-900 font-medium">
            <span data-testid="bulk-selected-count" className="text-blue-700 font-mono">
              {Object.values(selected).filter(Boolean).length}
            </span>{" "}
            selected · set daily budget
          </div>
          <input
            type="number"
            min="1"
            step="1"
            value={bulkBudget}
            onChange={(e) => setBulkBudget(e.target.value)}
            placeholder="e.g. 50"
            data-testid="bulk-budget-input"
            className="w-24 bg-white border border-slate-200 rounded px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-blue-500/40"
          />
          <button
            onClick={async () => {
              const budget = parseFloat(bulkBudget);
              if (!budget || budget < 1) return toast.error("Enter a budget ≥ $1");
              const ids = Object.entries(selected).filter(([, v]) => v).map(([k]) => k);
              setBulkBusy(true);
              try {
                const { data: res } = await api.post("/campaigns/bulk-budget", {
                  campaign_ids: ids,
                  daily_budget: budget,
                });
                if (res.ok_count === res.count) {
                  toast.success(`Updated ${res.ok_count} campaigns to ${fmtMoney(budget)}/day`);
                } else {
                  toast.warning(`${res.ok_count}/${res.count} succeeded — rest failed`);
                }
                setSelected({});
                setBulkBudget("");
                load();
              } catch (e) {
                toast.error(e?.response?.data?.detail || "Bulk update failed");
              } finally {
                setBulkBusy(false);
              }
            }}
            disabled={bulkBusy || !bulkBudget}
            data-testid="bulk-apply-btn"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-xs font-medium rounded px-3 py-1 transition-colors"
          >
            {bulkBusy ? "Applying…" : "Apply"}
          </button>
          <button
            onClick={() => setSelected({})}
            data-testid="bulk-clear-btn"
            className="text-xs text-slate-600 hover:text-slate-900 ml-auto"
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="rounded-md border border-slate-200 overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-medium bg-white border-b border-slate-200">
              <tr>
                {isAdmin && (
                  <th className="px-3 py-2.5 w-8">
                    <input
                      type="checkbox"
                      data-testid="bulk-select-all"
                      className="accent-blue-600"
                      checked={filtered.length > 0 && filtered.every((r) => selected[r.id])}
                      onChange={(e) => {
                        if (e.target.checked) {
                          const next = {};
                          filtered.forEach((r) => { next[r.id] = true; });
                          setSelected(next);
                        } else {
                          setSelected({});
                        }
                      }}
                    />
                  </th>
                )}
                <th className="px-3 py-2.5 text-left">Campaign</th>
                {isVisible("vertical") && <th className="px-3 py-2.5 text-left">Vertical</th>}
                {isVisible("daily_budget") && <th className="px-3 py-2.5 text-right">Budget</th>}
                {isVisible("spend") && <th className="px-3 py-2.5 text-right">Spent</th>}
                {isVisible("impressions") && <th className="px-3 py-2.5 text-right">Impressions</th>}
                {isVisible("cpm") && <th className="px-3 py-2.5 text-right">CPM</th>}
                {isVisible("clicks") && <th className="px-3 py-2.5 text-right">Clicks</th>}
                {isVisible("cpc") && <th className="px-3 py-2.5 text-right">CPC</th>}
                {isVisible("ctr") && <th className="px-3 py-2.5 text-right">CTR</th>}
                {isVisible("ad_conversions") && <th className="px-3 py-2.5 text-right" title="Meta-tracked conversions (e.g. Website Purchase)">Ad Conv</th>}
                {isVisible("cpaa") && <th className="px-3 py-2.5 text-right" title="Spend / Ad Conv">CPAA</th>}
                {isVisible("ad_rpc") && <th className="px-3 py-2.5 text-right" title="Revenue / Ad Conv">Ad RPC</th>}
                {isVisible("ad_conv_rate") && <th className="px-3 py-2.5 text-right" title="Ad Conv / Ad Clicks">Ad CONV%</th>}
                {isVisible("conversions") && <th className="px-3 py-2.5 text-right" title="S1 postback conversions">Conv (S1)</th>}
                {isVisible("cost_per_conv") && <th className="px-3 py-2.5 text-right" title="Spend / S1 Conv">CPA</th>}
                {isVisible("revenue") && <th className="px-3 py-2.5 text-right">Revenue</th>}
                {isVisible("s1_clicks") && <th className="px-3 py-2.5 text-right">S1 Clicks</th>}
                {isVisible("rpc") && <th className="px-3 py-2.5 text-right" title="Revenue / S1 Conv">RPC</th>}
                {isVisible("s1_conv_rate") && <th className="px-3 py-2.5 text-right" title="S1 Conv / Ad Clicks">CONV%</th>}
                {isVisible("rpm") && <th className="px-3 py-2.5 text-right">RPM</th>}
                {isVisible("rpwidget") && <th className="px-3 py-2.5 text-right">Widget Srch</th>}
                {isVisible("net_profit") && <th className="px-3 py-2.5 text-right" title="Revenue − Spend">Profit</th>}
                {isVisible("roi") && <th className="px-3 py-2.5 text-right" title="Profit / Spend">ROI</th>}
                {isVisible("roas") && <th className="px-3 py-2.5 text-right" title="Revenue / Spend">ROAS</th>}
                {isVisible("rpac") && <th className="px-3 py-2.5 text-right" title="Revenue / Ad Clicks">RPAC</th>}
                <th className="px-3 py-2.5 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {filtered.map((r) => (
                <Fragment key={r.id}>
                  <tr className="hover:bg-slate-100/40 transition-colors" data-testid={`row-${r.id}`}>
                    {isAdmin && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          data-testid={`bulk-select-${r.id}`}
                          className="accent-blue-600"
                          checked={!!selected[r.id]}
                          onChange={(e) => setSelected((s) => ({ ...s, [r.id]: e.target.checked }))}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">
                      <button onClick={() => toggleExpand(r)} className="flex items-center gap-1.5 text-slate-900 hover:text-blue-600 text-left" data-testid={`expand-${r.id}`}>
                        {expanded[r.id] ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                        <span className="truncate max-w-[240px]">{r.name}</span>
                      </button>
                    </td>
                    {isVisible("vertical") && <td className="px-3 py-2 text-slate-500 text-xs">{r.vertical || "—"}</td>}
                    {isVisible("daily_budget") && (
                      <td className="px-3 py-2 text-right font-mono">
                        {editing === r.id ? (
                          <div className="flex items-center gap-1 justify-end">
                            <input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              type="number"
                              data-testid={`budget-input-${r.id}`}
                              className="w-20 bg-white border border-slate-200 rounded px-1.5 py-0.5 text-xs text-right"
                              autoFocus
                            />
                            <button onClick={() => saveBudget(r)} data-testid={`save-budget-${r.id}`} className="text-emerald-600 text-xs hover:underline">save</button>
                            <button onClick={() => setEditing(null)} className="text-slate-500 text-xs">×</button>
                          </div>
                        ) : (
                          <button
                            onClick={() => { setEditing(r.id); setEditValue(String(r.daily_budget)); }}
                            data-testid={`edit-budget-${r.id}`}
                            className="text-slate-900 hover:text-blue-600"
                          >
                            {fmtMoney(r.daily_budget)}
                          </button>
                        )}
                      </td>
                    )}
                    {isVisible("spend") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtMoney(r.spend)}</td>}
                    {isVisible("impressions") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmt.format(r.impressions || 0)}</td>}
                    {isVisible("cpm") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtMoney(r.cpm)}</td>}
                    {isVisible("clicks") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmt.format(r.clicks || 0)}</td>}
                    {isVisible("cpc") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtMoney(r.cpc)}</td>}
                    {isVisible("ctr") && <td className="px-3 py-2 text-right font-mono text-slate-600">{r.impressions ? fmtPct((r.clicks || 0) / r.impressions * 100) : "—"}</td>}
                    {isVisible("ad_conversions") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmt.format(r.ad_conversions || 0)}</td>}
                    {isVisible("cpaa") && <td className="px-3 py-2 text-right font-mono text-slate-600">{r.ad_conversions ? fmtMoney(r.cpaa) : "—"}</td>}
                    {isVisible("ad_rpc") && <td className="px-3 py-2 text-right font-mono text-slate-600">{r.ad_conversions ? fmtMoney(r.ad_rpc) : "—"}</td>}
                    {isVisible("ad_conv_rate") && <td className="px-3 py-2 text-right font-mono text-slate-600">{r.clicks ? fmtPct(r.ad_conv_rate) : "—"}</td>}
                    {isVisible("conversions") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmt.format(r.conversions || 0)}</td>}
                    {isVisible("cost_per_conv") && <td className="px-3 py-2 text-right font-mono text-slate-600">{r.conversions ? fmtMoney(r.cost_per_conv) : "—"}</td>}
                    {isVisible("revenue") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtMoney(r.revenue)}</td>}
                    {isVisible("s1_clicks") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmt.format(r.s1_clicks || 0)}</td>}
                    {isVisible("rpc") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtMoney(r.rpc)}</td>}
                    {isVisible("s1_conv_rate") && <td className="px-3 py-2 text-right font-mono text-slate-600">{r.clicks ? fmtPct(r.s1_conv_rate) : "—"}</td>}
                    {isVisible("rpm") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtMoney(r.rpm)}</td>}
                    {isVisible("rpwidget") && <td className="px-3 py-2 text-right font-mono text-slate-600">{r.widget_searches ? fmtMoney((r.revenue || 0) / r.widget_searches) : "—"}</td>}
                    {isVisible("net_profit") && <td className={`px-3 py-2 text-right font-mono font-medium ${r.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(r.net_profit)}</td>}
                    {isVisible("roi") && <td className={`px-3 py-2 text-right font-mono ${r.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(r.roi)}</td>}
                    {isVisible("roas") && <td className={`px-3 py-2 text-right font-mono ${r.roas >= 1 ? "text-emerald-600" : "text-red-600"}`}>{(r.roas || 0).toFixed(2)}×</td>}
                    {isVisible("rpac") && <td className="px-3 py-2 text-right font-mono text-slate-600">{fmtMoney(r.rpac)}</td>}
                    <td className="px-3 py-2 text-center">
                      <button
                        onClick={() => toggleStatus(r)}
                        data-testid={`toggle-status-${r.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium border ${
                          r.status === "ACTIVE"
                            ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/20 hover:bg-emerald-500/20"
                            : "bg-amber-500/10 text-amber-600 border-amber-500/20 hover:bg-amber-500/20"
                        }`}
                      >
                        {r.status === "ACTIVE" ? <Pause className="w-2.5 h-2.5" /> : <Play className="w-2.5 h-2.5" />}
                        {r.status}
                      </button>
                    </td>
                  </tr>
                  {expanded[r.id] && expanded[r.id].map((a) => (
                    <tr key={a.id} className="bg-slate-50" data-testid={`adset-${a.id}`}>
                      {isAdmin && <td className="px-3 py-1.5" />}
                      <td className="px-3 py-1.5 pl-9 text-slate-500 text-xs">↳ {a.name}</td>
                      {isVisible("vertical") && <td className="px-3 py-1.5 text-slate-500 text-[11px]">{a.vertical}</td>}
                      {isVisible("daily_budget") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtMoney(a.daily_budget)}</td>}
                      {isVisible("spend") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtMoney(a.spend)}</td>}
                      {isVisible("impressions") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmt.format(a.impressions || 0)}</td>}
                      {isVisible("cpm") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtMoney(a.cpm)}</td>}
                      {isVisible("clicks") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmt.format(a.clicks || 0)}</td>}
                      {isVisible("cpc") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtMoney(a.cpc)}</td>}
                      {isVisible("ctr") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{a.impressions ? fmtPct((a.clicks || 0) / a.impressions * 100) : "—"}</td>}
                      {isVisible("ad_conversions") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmt.format(a.ad_conversions || 0)}</td>}
                      {isVisible("cpaa") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{a.ad_conversions ? fmtMoney(a.cpaa) : "—"}</td>}
                      {isVisible("ad_rpc") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{a.ad_conversions ? fmtMoney(a.ad_rpc) : "—"}</td>}
                      {isVisible("ad_conv_rate") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{a.clicks ? fmtPct(a.ad_conv_rate) : "—"}</td>}
                      {isVisible("conversions") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmt.format(a.conversions || 0)}</td>}
                      {isVisible("cost_per_conv") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{a.conversions ? fmtMoney(a.cost_per_conv) : "—"}</td>}
                      {isVisible("revenue") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtMoney(a.revenue)}</td>}
                      {isVisible("s1_clicks") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmt.format(a.s1_clicks || 0)}</td>}
                      {isVisible("rpc") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtMoney(a.rpc)}</td>}
                      {isVisible("s1_conv_rate") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{a.clicks ? fmtPct(a.s1_conv_rate) : "—"}</td>}
                      {isVisible("rpm") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtMoney(a.rpm)}</td>}
                      {isVisible("rpwidget") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{a.widget_searches ? fmtMoney((a.revenue || 0) / a.widget_searches) : "—"}</td>}
                      {isVisible("net_profit") && <td className={`px-3 py-1.5 text-right font-mono text-xs ${a.net_profit >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtMoney(a.net_profit)}</td>}
                      {isVisible("roi") && <td className={`px-3 py-1.5 text-right font-mono text-xs ${a.roi >= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtPct(a.roi)}</td>}
                      {isVisible("roas") && <td className={`px-3 py-1.5 text-right font-mono text-xs ${a.roas >= 1 ? "text-emerald-600" : "text-red-600"}`}>{(a.roas || 0).toFixed(2)}×</td>}
                      {isVisible("rpac") && <td className="px-3 py-1.5 text-right font-mono text-xs text-slate-500">{fmtMoney(a.rpac)}</td>}
                      <td className="px-3 py-1.5 text-center">
                        <span className="text-[10px] text-slate-500 font-mono">{a.status}</span>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td colSpan={visibleCols.size + (isAdmin ? 3 : 2)} className="px-4 py-12 text-center text-slate-500 text-sm">No campaigns match your filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <LaunchCampaignModal
        open={showLaunchModal}
        onClose={(didLaunch) => { setShowLaunchModal(false); if (didLaunch) load(); }}
        adAccounts={adAccounts}
        defaultAdAccountId={adAccountId}
      />
    </div>
  );
}
